import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ChatCartPaid } from "components/icons/Chat/ChatCartPaid";
import { LargeInfo } from "components/icons/LargeInfo";
import { FormikHelpers } from "formik";
import { couponSchema } from "formValidations/couponCodeSchema";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import React, { useContext, useEffect, useState } from "react";
import { useApplyInviteCouponMutation } from "store/apis/onboarding";
import { BackendError } from "types/utils/error";

const CouponVerifiedSuccessfully = () => (
  <div className="t-text-body-sm-italic t-text-dark_green">
    Verified successfully!
  </div>
);

export const getStoredCouponCode = (): string => {
  const storedCoupon = localStorage.getItem("couponCode");
  return storedCoupon && storedCoupon !== "null" ? storedCoupon : "";
};

const getCurrentUserId = (): string => {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  return currentUser?.uuid || "default";
};

export const CouponCodeVerify = () => {
  const { authtoken } = useContext(authContext);
  const { alertToast } = useToast();
  const [verifyCoupon, { data, isSuccess, isLoading }] =
    useApplyInviteCouponMutation();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const userId = authtoken?.uuid || getCurrentUserId();
  const initialCouponValue = getStoredCouponCode();

  const handleVerifyCoupon = async (
    values: { couponCode: string },
    formikHelpers?: FormikHelpers<{ couponCode: string }>
  ) => {
    try {
      const { couponCode } = values;
      const response = await verifyCoupon({
        couponCode,
        uuid: userId,
      }).unwrap();

      if (couponCode) {
        localStorage.setItem("couponCode", couponCode);
      }

      if (!response.is_valid_coupon && formikHelpers) {
        formikHelpers.setFieldError(
          "couponCode",
          "Incorrect coupon code, try again"
        );
      }
    } catch (e) {
      alertToast({
        message: (e as BackendError).data?.error?.message,
      });
    } finally {
      setHasSubmitted(true);
    }
  };

  const autoVerifyCoupon = async () => {
    if (!initialCouponValue) {
      return;
    }

    try {
      setIsAutoVerifying(true);
      await handleVerifyCoupon({ couponCode: initialCouponValue });
    } finally {
      setIsAutoVerifying(false);
    }
  };

  useEffect(() => {
    autoVerifyCoupon();
  }, []);

  const getRightComponent = () => {
    if (!isSuccess || !hasSubmitted) return null;

    if (data?.is_valid_coupon) {
      return <ChatCartPaid />;
    }

    return <LargeInfo className="t-text-red" />;
  };

  const rightComponent = getRightComponent();
  const isVerified = isSuccess && data?.is_valid_coupon && hasSubmitted;

  return (
    <FormikForm
      initialValues={{ couponCode: initialCouponValue }}
      onSubmit={handleVerifyCoupon}
      validationSchema={couponSchema}
    >
      {({ setFieldValue }) => {
        const removeCoupon = () => {
          localStorage.removeItem("couponCode");
          setFieldValue("couponCode", "");
          setHasSubmitted(false);
        };

        return (
          <div className="t-flex t-gap-2.5 t-items-start">
            <div className="t-w-full">
              <TextInput
                label="Enter coupon code (optional)"
                name="couponCode"
                placeholder="Enter coupon code"
                customSize="large"
                disabled={isVerified}
                rightComponent={rightComponent}
                autoFocus
                note={isVerified && <CouponVerifiedSuccessfully />}
              />
              <span className="t-text-text-30 t-text-body-sm-italic t-italic">
                This can also be added later in the platform.
              </span>
            </div>
            {!isAutoVerifying && (
              <div className="t-mt-6">
                {isVerified ? (
                  <Button
                    size="large"
                    customType="danger_outlined"
                    type="button"
                    onClick={removeCoupon}
                  >
                    Remove
                  </Button>
                ) : (
                  <Button
                    size="large"
                    disabled={isLoading}
                    isLoading={isLoading}
                  >
                    Verify code
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      }}
    </FormikForm>
  );
};
